import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { StatusBadge, Spinner } from "../../components/Brand";
import { currency, fmtDate } from "../../lib/status";
import { toast } from "sonner";

const NEXT_ACTION = {
    technician_assigned: { next: "technician_accepted", label: "Accept Job" },
    technician_accepted: { next: "technician_on_the_way", label: "Start Navigation" },
    technician_on_the_way: { next: "technician_arrived", label: "Mark as Arrived" },
    technician_arrived: { next: "job_started", label: "Start Job" },
};

export default function TechJobDetail() {
    const { id } = useParams();
    const [b, setB] = useState(null);
    const [busy, setBusy] = useState(false);
    const [photoUrl, setPhotoUrl] = useState("");
    const [spareItems, setSpareItems] = useState([{ name: "", price: "" }]);
    const [otpInput, setOtpInput] = useState("");
    const [otpRequested, setOtpRequested] = useState(false);
    const nav = useNavigate();

    const load = async () => {
        const { data } = await api.get(`/bookings/${id}`);
        setB(data);
    };
    useEffect(() => {
        load();
    }, [id]);

    if (!b) return <Spinner />;

    const action = NEXT_ACTION[b.status];

    const advance = async () => {
        if (!action) return;
        setBusy(true);
        try {
            await api.post(`/bookings/${b.id}/status`, { status: action.next });
            toast.success(`Status: ${action.next}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const addPhoto = async (kind) => {
        const url = photoUrl.trim();
        if (!url) return toast.error("Paste a photo URL");
        try {
            await api.post(`/bookings/${b.id}/photo`, { kind, url });
            toast.success(`${kind} photo added`);
            setPhotoUrl("");
            load();
        } catch (e) {
            toast.error("Failed");
        }
    };

    const reject = async () => {
        if (!window.confirm("Reject this job?")) return;
        try {
            await api.post(`/bookings/${b.id}/status`, {
                status: "cancelled",
                note: "Rejected by technician",
            });
            toast.success("Job rejected");
            nav("/technician");
        } catch (e) {
            toast.error("Cannot reject at this stage");
        }
    };

    const proposeSpareParts = async () => {
        const items = spareItems
            .filter((i) => i.name && i.price)
            .map((i) => ({ name: i.name, price: parseFloat(i.price) }));
        if (items.length === 0) return toast.error("Add at least one item with price");
        try {
            await api.post(`/bookings/${b.id}/spare-parts`, { items });
            toast.success("Spare parts sent to customer for approval");
            setSpareItems([{ name: "", price: "" }]);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const requestOtp = async () => {
        try {
            await api.post(`/bookings/${b.id}/otp/request`);
            toast.success("OTP sent to customer — ask them to share it with you");
            setOtpRequested(true);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const verifyOtp = async () => {
        if (otpInput.length !== 6) return toast.error("Enter the 6-digit code");
        try {
            await api.post(`/bookings/${b.id}/otp/verify`, { otp: otpInput });
            toast.success("Job completed");
            setOtpInput("");
            setOtpRequested(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid OTP");
        }
    };

    const openMap = () => {
        const a = b.address;
        if (a?.lat && a?.lng) {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${a.lat},${a.lng}`,
                "_blank",
            );
        } else {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a?.line1 + ", " + a?.city)}`,
                "_blank",
            );
        }
    };

    return (
        <div className="space-y-5">
            <button onClick={() => nav(-1)} className="text-sm text-[#555]">
                ← Back
            </button>

            <div className="bg-white p-5">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="lx-label text-[#B2912A]">{b.booking_number}</span>
                    <StatusBadge status={b.status} />
                </div>
                <div className="font-serif text-2xl text-[#180A21]">
                    {b.service?.name}
                </div>
                <div className="text-sm text-[#555] mt-1">
                    {fmtDate(b.scheduled_at)} · {b.service?.duration_minutes} min
                </div>
            </div>

            <div className="bg-white p-5">
                <div className="lx-label text-[#B2912A] mb-3">Customer</div>
                <div className="font-serif text-lg text-[#180A21]">{b.customer?.name}</div>
                <div className="text-sm text-[#555]">{b.customer?.phone || "—"}</div>
            </div>

            <div className="bg-white p-5">
                <div className="lx-label text-[#B2912A] mb-3">Address</div>
                <div className="text-sm text-[#180A21]">{b.address?.line1}</div>
                {b.address?.line2 && (
                    <div className="text-sm text-[#555]">{b.address.line2}</div>
                )}
                <div className="text-sm text-[#555] mt-1">
                    {b.address?.city}, {b.address?.country}
                </div>
                <button
                    onClick={openMap}
                    data-testid="tech-navigate-btn"
                    className="mt-3 w-full bg-[#180A21] text-white py-3 text-sm font-semibold tracking-wider uppercase"
                >
                    Navigate (Google Maps)
                </button>
            </div>

            {b.notes && (
                <div className="bg-white p-5">
                    <div className="lx-label text-[#B2912A] mb-2">Customer notes</div>
                    <div className="text-sm text-[#180A21]">{b.notes}</div>
                </div>
            )}

            <div className="bg-white p-5">
                <div className="lx-label text-[#B2912A] mb-3">Job photos</div>
                <input
                    placeholder="Paste photo URL"
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    className="w-full p-2.5 border border-[#E5E2DC] text-sm mb-2"
                    data-testid="tech-photo-url"
                />
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => addPhoto("before")}
                        data-testid="tech-photo-before"
                        className="py-2.5 text-xs uppercase tracking-wider border border-[#180A21] text-[#180A21]"
                    >
                        + Before
                    </button>
                    <button
                        onClick={() => addPhoto("after")}
                        data-testid="tech-photo-after"
                        className="py-2.5 text-xs uppercase tracking-wider border border-[#180A21] text-[#180A21]"
                    >
                        + After
                    </button>
                </div>
                {(b.before_photos?.length > 0 || b.after_photos?.length > 0) && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        {b.before_photos?.map((p, i) => (
                            <img key={"b" + i} src={p} alt="" className="w-full aspect-square object-cover" />
                        ))}
                        {b.after_photos?.map((p, i) => (
                            <img key={"a" + i} src={p} alt="" className="w-full aspect-square object-cover" />
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-white p-5">
                <div className="lx-label text-[#B2912A] mb-3">Payment</div>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-[#555]">Method</span>
                    <span className="text-sm uppercase tracking-wider text-[#180A21]">
                        {b.payment_method}
                    </span>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F3F2F0]">
                    <div className="lx-label text-[#180A21]">Your payout</div>
                    <div className="font-serif text-2xl text-[#180A21]">
                        {currency(b.technician_payout, b.currency)}
                    </div>
                </div>
            </div>

            {/* Spare parts (visible when arrived or job_started) */}
            {(b.status === "technician_arrived" || b.status === "job_started") && (
                <div className="bg-white p-5">
                    <div className="lx-label text-[#B2912A] mb-3">Spare parts / materials</div>
                    {b.spare_parts_status === "pending" ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 text-sm">
                            Awaiting customer approval —
                            {b.pending_spare_parts?.map((p, i) => (
                                <div key={i} className="flex justify-between mt-1">
                                    <span>{p.name}</span>
                                    <span>{currency(p.price, b.currency)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {b.spare_parts?.length > 0 && (
                                <div className="mb-3 text-sm">
                                    {b.spare_parts.map((p, i) => (
                                        <div key={i} className="flex justify-between text-[#180A21]">
                                            <span>{p.name}</span>
                                            <span>{currency(p.price, b.currency)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {spareItems.map((it, idx) => (
                                <div key={idx} className="grid grid-cols-5 gap-2 mb-2">
                                    <input
                                        data-testid={`tech-spare-name-${idx}`}
                                        placeholder="Item name"
                                        value={it.name}
                                        onChange={(e) => {
                                            const next = [...spareItems];
                                            next[idx].name = e.target.value;
                                            setSpareItems(next);
                                        }}
                                        className="col-span-3 p-2 border border-[#E5E2DC] text-sm"
                                    />
                                    <input
                                        data-testid={`tech-spare-price-${idx}`}
                                        type="number"
                                        step="0.01"
                                        placeholder="Price"
                                        value={it.price}
                                        onChange={(e) => {
                                            const next = [...spareItems];
                                            next[idx].price = e.target.value;
                                            setSpareItems(next);
                                        }}
                                        className="col-span-2 p-2 border border-[#E5E2DC] text-sm"
                                    />
                                </div>
                            ))}
                            <div className="flex gap-2 mt-2">
                                <button
                                    data-testid="tech-spare-add-row"
                                    onClick={() =>
                                        setSpareItems([...spareItems, { name: "", price: "" }])
                                    }
                                    className="text-xs text-[#180A21] underline"
                                >
                                    + Add row
                                </button>
                                <button
                                    data-testid="tech-spare-submit"
                                    onClick={proposeSpareParts}
                                    className="ml-auto px-4 py-2 text-xs bg-[#180A21] text-[#D4AF37]"
                                >
                                    Send for approval
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* OTP completion */}
            {b.status === "job_started" && (
                <div className="bg-white p-5">
                    <div className="lx-label text-[#B2912A] mb-3">Customer OTP verification</div>
                    <p className="text-xs text-[#555] mb-3">
                        Generate a code → ask the customer for it → enter below to confirm
                        completion.
                    </p>
                    <button
                        data-testid="tech-otp-request"
                        onClick={requestOtp}
                        className="w-full py-2.5 text-xs border border-[#180A21] text-[#180A21] mb-3"
                    >
                        {otpRequested ? "Re-generate OTP" : "Request OTP from customer"}
                    </button>
                    <div className="flex gap-2">
                        <input
                            data-testid="tech-otp-input"
                            value={otpInput}
                            onChange={(e) =>
                                setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))
                            }
                            placeholder="6-digit code"
                            inputMode="numeric"
                            className="flex-1 p-2.5 border border-[#E5E2DC] text-center font-mono text-lg tracking-widest"
                        />
                        <button
                            data-testid="tech-otp-verify"
                            onClick={verifyOtp}
                            disabled={otpInput.length !== 6}
                            className="px-4 py-2.5 text-xs bg-[#D4AF37] text-[#180A21] font-semibold disabled:opacity-40"
                        >
                            Verify & complete
                        </button>
                    </div>
                </div>
            )}

            {/* Sticky CTA */}
            <div className="sticky bottom-20 left-0 right-0 space-y-2">
                {action && (
                    <button
                        data-testid="tech-advance-btn"
                        onClick={advance}
                        disabled={busy}
                        className="lx-btn-gold w-full py-4 text-base font-semibold disabled:opacity-50"
                    >
                        {busy ? "…" : action.label} →
                    </button>
                )}
                {b.status === "technician_assigned" && (
                    <button
                        onClick={reject}
                        data-testid="tech-reject-btn"
                        className="w-full py-3 border border-red-300 text-red-700 text-sm bg-white"
                    >
                        Reject Job
                    </button>
                )}
            </div>
        </div>
    );
}
